/**
 * ItemRepository - Repositório de Itens de Ambiente
 * Persiste itens dos ambientes no IndexedDB
 */

import { openDatabase } from './IndexedDBSetup';

const STORE_NAME = 'items';

/**
 * Gera ID local único
 */
const generateLocalId = () => {
  const now = new Date();
  const dateStr = now.toISOString().replace(/[-:T]/g, '').slice(0, 14);
  const random = Math.random().toString(36).substring(2, 5).toUpperCase();
  return `LOC-ITEM-${dateStr}-${random}`;
};

/**
 * Cria novo item
 */
export const create = async (environmentId, localEnvironmentId, itemData) => {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);

    const localId = generateLocalId();
    const now = new Date().toISOString();

    const item = {
      ...itemData,
      id: localId,
      localId,
      environmentId,
      localEnvironmentId,
      syncStatus: 'pending',
      createdAt: now,
      updatedAt: now,
      serverId: null,
    };

    const request = store.add(item);

    request.onsuccess = () => {
      resolve(item);
    };

    request.onerror = () => {
      reject(new Error(`Erro ao criar item: ${request.error}`));
    };
  });
};

/**
 * Atualiza item existente
 */
export const update = async (id, data) => {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);

    const getRequest = store.get(id);

    getRequest.onsuccess = () => {
      const existing = getRequest.result;

      if (!existing) {
        reject(new Error(`Item com ID ${id} não encontrado`));
        return;
      }

      const updated = {
        ...existing,
        ...data,
        id: existing.id,
        localId: existing.localId,
        syncStatus: 'pending',
        updatedAt: new Date().toISOString(),
      };

      const putRequest = store.put(updated);

      putRequest.onsuccess = () => resolve(updated);
      putRequest.onerror = () => reject(new Error(`Erro ao atualizar item: ${putRequest.error}`));
    };

    getRequest.onerror = () => reject(new Error(`Erro ao buscar item: ${getRequest.error}`));
  });
};

/**
 * Remove item
 */
export const remove = async (id) => {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.delete(id);

    request.onsuccess = () => resolve();
    request.onerror = () => reject(new Error(`Erro ao remover item: ${request.error}`));
  });
};

/**
 * Busca item por ID
 */
export const getById = async (id) => {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.get(id);

    request.onsuccess = () => resolve(request.result || null);
    request.onerror = () => reject(new Error(`Erro ao buscar item: ${request.error}`));
  });
};

/**
 * Busca item por localId
 */
export const getByLocalId = async (localId) => {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const index = store.index('localId');
    const request = index.get(localId);

    request.onsuccess = () => resolve(request.result || null);
    request.onerror = () => reject(new Error(`Erro ao buscar item: ${request.error}`));
  });
};

/**
 * Lista itens de um ambiente (por localEnvironmentId)
 */
export const getByEnvironmentLocalId = async (localEnvironmentId) => {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const index = store.index('localEnvironmentId');
    const request = index.getAll(localEnvironmentId);

    request.onsuccess = () => {
      const results = request.result || [];
      results.sort((a, b) => (a.order || 0) - (b.order || 0));
      resolve(results);
    };
    request.onerror = () => reject(new Error(`Erro ao listar itens: ${request.error}`));
  });
};

/**
 * Lista itens pendentes de sync
 */
export const getPending = async () => {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const index = store.index('syncStatus');
    const request = index.getAll('pending');

    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(new Error(`Erro ao buscar pendientes: ${request.error}`));
  });
};

/**
 * Atualiza status de sync
 */
export const updateSyncStatus = async (id, status, serverId = null) => {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const getRequest = store.get(id);

    getRequest.onsuccess = () => {
      const existing = getRequest.result;
      if (!existing) {
        reject(new Error(`Item com ID ${id} não encontrado`));
        return;
      }

      const updated = {
        ...existing,
        syncStatus: status,
        serverId: serverId !== null ? serverId : existing.serverId,
        syncedAt: status === 'synced' ? new Date().toISOString() : existing.syncedAt,
      };

      const putRequest = store.put(updated);
      putRequest.onsuccess = () => resolve(updated);
      putRequest.onerror = () => reject(new Error(`Erro ao atualizar sync status: ${putRequest.error}`));
    };

    getRequest.onerror = () => reject(new Error(`Erro ao buscar item: ${getRequest.error}`));
  });
};

/**
 * Atualiza serverId após sincronização
 */
export const updateServerId = async (localId, serverId) => {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const index = store.index('localId');
    const getRequest = index.get(localId);

    getRequest.onsuccess = () => {
      const existing = getRequest.result;
      if (!existing) {
        reject(new Error(`Item com localId ${localId} não encontrado`));
        return;
      }

      const updated = { ...existing, serverId, syncStatus: 'synced' };
      const putRequest = store.put(updated);
      putRequest.onsuccess = () => resolve(updated);
      putRequest.onerror = () => reject(new Error(`Erro ao atualizar serverId: ${putRequest.error}`));
    };

    getRequest.onerror = () => reject(new Error(`Erro ao buscar item: ${getRequest.error}`));
  });
};

/**
 * Remove todos os itens de um ambiente
 */
export const removeByEnvironmentLocalId = async (localEnvironmentId) => {
  const db = await openDatabase();
  const items = await getByEnvironmentLocalId(localEnvironmentId);

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    let completed = 0;

    if (items.length === 0) {
      resolve();
      return;
    }

    items.forEach(item => {
      const request = transaction.objectStore(STORE_NAME).delete(item.id);
      request.onsuccess = () => {
        completed++;
        if (completed === items.length) resolve();
      };
      request.onerror = () => reject(new Error(`Erro ao remover item ${item.id}`));
    });
  });
};

/**
 * Atualiza serverIds para todos os itens de um ambiente após sync
 */
export const updateServerIdsForEnvironment = async (localEnvironmentId, serverEnvironmentId) => {
  const db = await openDatabase();
  const items = await getByEnvironmentLocalId(localEnvironmentId);

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    let completed = 0;

    if (items.length === 0) {
      resolve([]);
      return;
    }

    const updatedItems = [];

    items.forEach(item => {
      const store = transaction.objectStore(STORE_NAME);
      const getRequest = store.get(item.id);

      getRequest.onsuccess = () => {
        const existing = getRequest.result;
        const updated = { ...existing, serverId: serverEnvironmentId, syncStatus: 'synced' };
        const putRequest = store.put(updated);
        putRequest.onsuccess = () => {
          updatedItems.push(updated);
          completed++;
          if (completed === items.length) resolve(updatedItems);
        };
        putRequest.onerror = () => reject(new Error(`Erro ao atualizar item ${item.id}`));
      };
    });
  });
};

export default {
  create,
  update,
  remove,
  getById,
  getByLocalId,
  getByEnvironmentLocalId,
  getPending,
  updateSyncStatus,
  updateServerId,
  removeByEnvironmentLocalId,
  updateServerIdsForEnvironment,
};
